import {
  Box,
  IconButton,
  InputAdornment,
  Typography,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import TextField from 'components/text-field';
import { TextButton } from 'components/TextButton';
import React, { useEffect } from 'react';
import useLostPassword from '../hooks/lost-password-hooks';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import validations from 'utils/validations';

const schema = z.object({
  username: validations.email('Invalid email', 'required'),
  secret: z.string().optional(),
  password: z.string().optional(),
  confirm: z.string().optional(),
  step: z.string().optional(),
  lastStep: z.boolean().optional(),
}).superRefine((data, ctx) => {
  if (!data.lastStep) return;

  if (!data.secret) {
    ctx.addIssue({ path: ['secret'], code: z.ZodIssueCode.custom, message: 'Secret is required' });
  }
  if (!data.password) {
    ctx.addIssue({ path: ['password'], code: z.ZodIssueCode.custom, message: 'Password is required' });
  }
  if (!data.confirm) {
    ctx.addIssue({ path: ['confirm'], code: z.ZodIssueCode.custom, message: 'Confirm password is required' });
  } else if (data.confirm !== data.password) {
    ctx.addIssue({ path: ['confirm'], code: z.ZodIssueCode.custom, message: 'Passwords do not match' });
  }
});

const useStyles = makeStyles((theme: any) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    instructions: {
      color: theme.palette.grey[400],
    },
  })
);

type LostPasswordProps = {
  handleReset: () => void;
  initialValues?: { email: string; secret: string };
  fieldClassName?: string;
};

const LostPassword = ({ handleReset, initialValues, fieldClassName }: LostPasswordProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const theme: any = useTheme();
  const linkColor = theme.palette.secondary.main;
  const hasInitialValues = Boolean(initialValues?.email && initialValues?.secret);
  const { handleSubmitLostPassword, lastStep, handleShowPassword, showPasswords, toggleLastStep } =
    useLostPassword(handleReset, hasInitialValues);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid, isSubmitting },
    setValue,
    trigger,
  } = useForm({
    shouldUnregister: true,
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      username: initialValues?.email || '',
      secret: initialValues?.secret || '',
      password: '',
      confirm: '',
      lastStep: hasInitialValues,
    },
  });

  const handleToggleLastStep = () => {
    toggleLastStep();
    setValue('lastStep', !lastStep, { shouldValidate: true });
    trigger();
  };

  useEffect(() => {
    if (lastStep) {
      setValue('lastStep', lastStep, { shouldValidate: true });
    }
  }, [setValue, lastStep]);

  const passwordInputProps = {
    type: showPasswords ? 'text' : 'password',
    endAdornment: (
      <InputAdornment position="end">
        <IconButton
          onClick={() => handleShowPassword()}
          edge="end"
          tabIndex={-1}
          aria-label={t('auth:password')}
          disabled={isSubmitting}
          size="large"
        >
          <FontAwesomeIcon icon={showPasswords ? 'eye-slash' : 'eye'} />
        </IconButton>
      </InputAdornment>
    ),
  };

  return (
    <Box className={classes.root}>
      <TextButton
        text={t('auth:back')}
        icon="arrow-left"
        buttonColor="secondary"
        textColor={linkColor}
        customStyle={{ alignSelf: 'flex-start' }}
        disabled={isSubmitting}
        onClick={handleReset}
      />
      <Typography variant="body2" className={classes.instructions}>
        {t(lastStep ? 'auth:copy-secret-message' : 'auth:type-email-message')}
      </Typography>
      <Controller
        name="username"
        control={control}
        render={({ field: { ref, ...field } }) => (
          <TextField
            customClassName={fieldClassName}
            {...field}
            inputRef={ref}
            inputProps={{
              autoComplete: 'new-password',
              form: {
                autoComplete: 'off',
              },
            }}
            label={t('auth:email')}
            disabled={isSubmitting}
            error={!!errors.username}
            helperText={errors.username?.message}
          />
        )}
      />
      {lastStep && (
        <>
          <Controller
            name="secret"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <TextField
                customClassName={fieldClassName}
                {...field}
                inputRef={ref}
                label={t('auth:secret')}
                disabled={isSubmitting}
                error={!!errors.secret}
                helperText={errors.secret?.message}
              />
            )}
          />
          <Controller
            name="password"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <TextField
                customClassName={fieldClassName}
                {...field}
                inputRef={ref}
                label={t('auth:password')}
                disabled={isSubmitting}
                error={!!errors.password}
                helperText={errors.password?.message}
                InputProps={passwordInputProps}
              />
            )}
          />
          <Controller
            name="confirm"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <TextField
                customClassName={fieldClassName}
                {...field}
                inputRef={ref}
                label={t('auth:confirm-password')}
                disabled={isSubmitting}
                error={!!errors.confirm}
                helperText={errors.confirm?.message}
                InputProps={passwordInputProps}
              />
            )}
          />
        </>
      )}
      <TextButton
        text={t(lastStep ? 'auth:request-new-secret' : 'auth:already-have-secret')}
        buttonColor="secondary"
        textColor={linkColor}
        customStyle={{ alignSelf: 'flex-end' }}
        disabled={isSubmitting}
        onClick={handleToggleLastStep}
      />
      <ContainedButton
        text={t(lastStep ? 'auth:change-password' : 'auth:request-secret')}
        ariaLabel={t(lastStep ? 'auth:change-password' : 'auth:request-secret')}
        width="100%"
        isLoading={isSubmitting}
        disabled={!isValid || isSubmitting}
        onClick={handleSubmit(handleSubmitLostPassword)}
      />
    </Box>
  );
};

export default LostPassword;
